/**
 * Light markdown for journal entries (#468).
 *
 * PARITY PAIR with mobile/src/services/journalMarkdown.js -- byte
 * identical. This file is PURE and knows nothing about rendering, which
 * is the whole reason it can be shared: web renders the tokens as HTML
 * elements and React Native renders them as <Text>, from one parser.
 * Two parsers would be two dialects, and users find the difference.
 *
 * ── IT PRODUCES TOKENS, NEVER HTML ───────────────────────────────────
 *
 * This is a security decision, not a style one, and it is the reason
 * there is no `marked` + DOMPurify here.
 *
 * #290 sends journal text into a PUBLIC community composer. That is the
 * only surface in this app where one user's writing renders in another
 * user's browser, which makes it the highest-risk string in the
 * codebase. A markdown renderer that emits an HTML string needs
 * dangerouslySetInnerHTML and a sanitizer that is correct forever.
 *
 * Emitting tokens means the renderer never builds HTML at all. React
 * escapes the text content of every element it creates, so a body
 * containing <script> renders as the characters "<script>". Not
 * sanitized -- IMPOSSIBLE. There is no configuration to get wrong and
 * no dependency to keep current.
 *
 * It also happens to be the only shape that ports: React Native has no
 * innerHTML, so an HTML-string renderer would have needed a completely
 * separate mobile implementation, which is how two dialects start.
 *
 * ── WHAT IT SUPPORTS, AND WHY SO LITTLE ──────────────────────────────
 *
 * **bold**, *italic*, - bullets, and - [ ] checkboxes.
 *
 * The checkboxes are the actual reason this exists. "Things to bring
 * next visit" is one of the most natural journal uses there is, and a
 * plain-text list of dashes cannot be ticked off. Everything else is
 * close to free once a parser exists.
 *
 * No headings, links, images, tables, code or blockquotes. A journal is
 * written fast, standing up, sometimes in gloves -- syntax people have
 * to remember is syntax that will not get used, and every construct is
 * another thing to render identically on two platforms.
 *
 * ── LINE NUMBERS RIDE ALONG ──────────────────────────────────────────
 *
 * Every checkbox carries the index of the source line it came from, so
 * ticking one can rewrite exactly that line. Without it, toggling would
 * have to match on text -- and two identical "- [ ] smoker" lines would
 * make the wrong one tick.
 */

const BULLET = /^\s*[-*]\s+(.*)$/;
const CHECKBOX = /^\s*[-*]\s+\[([ xX])\]\s*(.*)$/;

/** Inline **bold** and *italic*, as a flat list of spans. */
function parseSpans(text) {
  const spans = [];
  // One pass, longest marker first: ** before *, or "**a**" is read as
  // an empty italic followed by a stray asterisk.
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) spans.push({ type: "text", text: text.slice(last, m.index) });
    if (m[1] !== undefined) spans.push({ type: "strong", text: m[1] });
    else spans.push({ type: "em", text: m[2] });
    last = re.lastIndex;
  }
  if (last < text.length) spans.push({ type: "text", text: text.slice(last) });
  return spans.length ? spans : [{ type: "text", text: "" }];
}

/**
 * Parse an entry body into blocks.
 *
 * Blocks are { type: "p", spans } or { type: "list", items }, where an
 * item is { spans, checked: true | false | null, line }. `checked` is
 * null for an ordinary bullet -- distinct from false, which is an
 * unticked box and renders a control.
 */
export function parseJournal(body) {
  const lines = String(body ?? "").split("\n");
  const blocks = [];
  let list = null;
  let para = null;

  const closeList = () => { if (list) { blocks.push(list); list = null; } };
  const closePara = () => { if (para) { blocks.push(para); para = null; } };

  lines.forEach((raw, line) => {
    const checkbox = raw.match(CHECKBOX);
    const bullet = raw.match(BULLET);

    if (checkbox || bullet) {
      closePara();
      if (!list) list = { type: "list", items: [] };
      list.items.push({
        spans: parseSpans(checkbox ? checkbox[2] : bullet[1]),
        checked: checkbox ? checkbox[1].toLowerCase() === "x" : null,
        line,
      });
      return;
    }

    closeList();
    if (raw.trim() === "") {
      closePara();
      return;
    }
    // Consecutive plain lines stay ONE paragraph, with the newlines
    // kept: people type a journal entry as lines, not as prose, and
    // reflowing what they wrote is not rendering it.
    if (!para) para = { type: "p", spans: [], lines: [] };
    para.lines.push(raw);
  });

  closeList();
  closePara();

  // Spans are parsed per paragraph once its lines are known.
  return blocks.map((b) =>
    b.type === "p" ? { type: "p", spans: parseSpans(b.lines.join("\n")) } : b);
}

/**
 * Tick or untick the checkbox on one line, returning the new body.
 *
 * REWRITES THE SOURCE TEXT, because that is where the state lives.
 * A side table would desynchronise the moment anyone edited the body,
 * and the tick has to survive an export -- an exported journal whose
 * checklists have all come unticked is a worse export than one with no
 * checklists.
 *
 * Returns the body unchanged if that line is not a checkbox, so a stale
 * click after an edit is a no-op rather than a corruption.
 */
export function toggleCheckbox(body, line) {
  const lines = String(body ?? "").split("\n");
  if (line < 0 || line >= lines.length) return body;

  const m = lines[line].match(CHECKBOX);
  if (!m) return body;

  const ticked = m[1].toLowerCase() === "x";
  lines[line] = lines[line].replace(/\[([ xX])\]/, ticked ? "[ ]" : "[x]");
  return lines.join("\n");
}

/** Does this entry contain anything the renderer would style? */
export function hasMarkup(body) {
  const text = String(body ?? "");
  return /\*\*[^*]+\*\*|\*[^*]+\*/.test(text)
    || text.split("\n").some((l) => BULLET.test(l));
}
